import { ArrowForward } from "@mui/icons-material";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Typography,
} from "@mui/material";
import { Box, Container, Grid } from "@mui/system";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import QuoteModal from "../quote/QuoteModal";

import tshirtFront from "../../assets/products/tshirtFront.png";
import sweatshirtFront from "../../assets/products/sweatshirtFront.png";
import hoodieFront from "../../assets/products/hoodieFront.png";
import zipHoodieFront from "../../assets/products/ziphoodFront.png";
import dtfImage from "../../assets/products/dtf-transfer.jpg";
import stickerImage from "../../assets/products/klistremerker.jpg";
import Wave from "../wave";
import theme from "../../styles/theme";
import { getApparelColorHexes } from "../../config/products/apparelColors";

// Cycles through garment colors without letting the color layer bleed through
// transparent corner pixels in the source product image.
function CyclingProductImage({
  src,
  alt,
  colors,
  interval = 1800,
  className,
  sx,
}) {
  const [colorIdx, setColorIdx] = useState(0);
  const idxRef = useRef(0);
  const activeColor = colors?.[colorIdx];

  useEffect(() => {
    if (!colors?.length) return;
    const id = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % colors.length;
      setColorIdx(idxRef.current);
    }, interval);
    return () => clearInterval(id);
  }, [colors, interval]);

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <Box
        className={className}
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "100%",
          transition: "transform 220ms ease",
          willChange: "transform",
        }}
      >
        {activeColor ? (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: "10px",
              borderRadius: "10px",
              backgroundColor: activeColor,
              transition: "background-color 0.6s ease",
              pointerEvents: "none",
            }}
          />
        ) : null}
        <CardMedia
          component="img"
          loading="lazy"
          image={src}
          alt={alt}
          sx={{
            ...sx,
            width: "auto",
            maxWidth: "100%",
            position: "relative",
            backgroundColor: "transparent",
          }}
        />
      </Box>
    </Box>
  );
}

const ProductsSection = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [quoteProduct, setQuoteProduct] = useState(null);

  const handleSeeMore = (item) => {
    // Use whatever destination you want "Se mer" to go to:
    navigate(item.href);
    // TEMP (until you add hrefs): do nothing
  };

  const products = [
    {
      tag: t("Home.Products.Tag.Textile", "Tekstil"),
      title: t("Home.Products.Items.OrganicTshirt.Title", "Organic T-Shirt"),
      desc: t(
        "Home.Products.Items.OrganicTshirt.Desc",
        "Unisex t-skjorte i 100 % økologisk bomull. GOTS-sertifisert.",
      ),
      image: tshirtFront,
      colors: getApparelColorHexes("tshirt"),
      price: null,
      href: "/products/tshirt",
    },
    {
      tag: t("Home.Products.Tag.Textile", "Tekstil"),
      title: t(
        "Home.Products.Items.ClassicSweatshirt.Title",
        "Classic Sweatshirt",
      ),
      desc: t(
        "Home.Products.Items.ClassicSweatshirt.Desc",
        "Bestselgende klassisk sweatshirt i høy kvalitet.",
      ),
      image: sweatshirtFront,
      colors: getApparelColorHexes("sweatshirt"),
      price: null,
      href: "/products/sweatshirt",
    },
    {
      tag: t("Home.Products.Tag.Textile", "Tekstil"),
      title: t("Home.Products.Items.OrganicHoodie.Title", "Organic Hoodie"),
      desc: t(
        "Home.Products.Items.OrganicHoodie.Desc",
        "Klassisk unisex hettegenser i økologisk bomull og resirkulert polyester. GOTS-sertifisert.",
      ),
      image: hoodieFront,
      colors: getApparelColorHexes("hoodie"),
      price: null,
      href: "/products/hoodie",
    },
    {
      tag: t("Home.Products.Tag.Textile", "Tekstil"),
      title: t("Home.Products.Items.ZipHoodie.Title", "Zip Hoodie"),
      desc: t(
        "Home.Products.Items.ZipHoodie.Desc",
        "Klassisk unisex hettejakke med glidelås, hette og stikklommer.",
      ),
      image: zipHoodieFront,
      colors: getApparelColorHexes("zip-hoodie"),
      price: null,
      href: "/products/zip-hoodie",
    },
    {
      tag: t("Home.Products.Tag.Print", "Trykk"),
      title: t("Home.Products.Items.Dtf.Title", "DTF Transfertrykk"),
      desc: t(
        "Home.Products.Items.Dtf.Desc",
        "Fullfarge DTF transfer for klær og tekstiler. Klar til varmepresse.",
      ),
      image: dtfImage,
      price: null,
      href: "/products/dtf",
    },
    {
      tag: t("Home.Products.Tag.Profiling", "Profilering"),
      title: t(
        "Home.Products.Items.Stickers.Title",
        "Klistremerker med eget design",
      ),
      desc: t(
        "Home.Products.Items.Stickers.Desc",
        "Høykvalitets vinylklistremerker med eget design for profilering og markedsføring.",
      ),
      image: stickerImage,
      price: null,
      href: "/products/stickers",
    },
  ];

  return (
    <>
      <Box
        id="products"
        sx={{ pb: { xs: 6, md: 10 }, backgroundColor: "background.default2" }}
      >
        <Container maxWidth="xl">
          <Chip
            label={t("Home.Products.ChipTitle", "Våre Produkter")}
            color="secondary"
            data-aos="fade-up"
            sx={{
              marginBottom: 1.5,
            }}
          />

          <Box
            data-aos="fade-up"
            sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}
          >
            <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
              {t("Home.Products.Title", "Profilprodukter & Tekstiltrykk")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ opacity: 0.8, maxWidth: 780, mx: "auto" }}
            >
              {t(
                "Home.Products.Subtitle",
                "Perfekt for firmaprofilering, events, arbeidsklær eller giveaways. Send oss din logo – vi lager en gratis forhåndsvisning.",
              )}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {products.map((item, index) => (
              <Grid key={item.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  onClick={() => handleSeeMore(item)}
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    height: "100%",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 180ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 38px rgba(0,0,0,0.12)",
                    },
                    "&:hover .productImage": {
                      transform: "scale(1.06)",
                    },
                    "&:hover .seeMoreButton": {
                      color: "primary.main",
                    },
                    "&:hover .seeMoreArrow": {
                      transform: "translateX(4px)",
                    },
                    "& .seeMoreArrow": {
                      transition: "transform 180ms ease",
                    },
                    "& .seeMoreButton": {
                      transition: "color 180ms ease",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      p: 2,
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Chip
                      label={item.tag}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        zIndex: 2,
                        fontWeight: 600,
                        borderRadius: 999,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                      }}
                      color="primary"
                    />

                    <CyclingProductImage
                      src={item.image}
                      alt={item.title}
                      colors={item.colors}
                      className="productImage"
                      sx={{
                        height: 220,
                        objectFit: "contain",
                        borderRadius: 2,
                        transition: "transform 220ms ease",
                        willChange: "transform",
                      }}
                    />
                  </Box>

                  <CardContent sx={{ textAlign: "left", flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.75 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {item.desc}
                    </Typography>

                    {item.price ? (
                      <Typography
                        variant="body1"
                        fontWeight={800}
                        sx={{ mt: 2 }}
                      >
                        {item.price}
                      </Typography>
                    ) : null}
                  </CardContent>

                  <CardActions
                    sx={{
                      px: 2,
                      pb: 2,
                      pt: 0,
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      variant="text"
                      sx={{ fontWeight: 600, textTransform: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuoteProduct(item.title);
                      }}
                    >
                      {t("Home.Products.AskOffer", "Be om tilbud")}
                    </Button>

                    <Button
                      component={RouterLink}
                      to={item.href}
                      variant="text"
                      endIcon={
                        <ArrowForward
                          className="seeMoreArrow"
                          sx={{ height: "16px" }}
                        />
                      }
                      className="seeMoreButton"
                      sx={{
                        textTransform: "none",
                        fontWeight: 400,
                        color: "text.secondary", // grey
                        "& .MuiButton-endIcon": { ml: 0.5 },
                        "&:hover": { backgroundColor: "transparent" }, // keep clean
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("Home.Products.SeeMore", "Se mer")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center", mt: { xs: 5, md: 7 } }}>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
              {t(
                "Home.Products.FooterLine",
                "Lokal produksjon · Profesjonell profilering · Enkel bestilling",
              )}
            </Typography>

            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1.4,
                textTransform: "none",
                fontWeight: 500,
              }}
              onClick={() =>
                setQuoteProduct(
                  t("Home.Products.Title", "Profilprodukter & Tekstiltrykk"),
                )
              }
            >
              {t("Home.Products.BottomCta", "Kontakt oss for tilbud")}
            </Button>
          </Box>
        </Container>
      </Box>
      <Wave
        amplitude={60}
        duration={10}
        fillStyle={theme.palette.background.default}
        frequency={2}
        waveHeightRatio={0.3}
        backgroundColor={theme.palette.background.default2}
      />

      <QuoteModal
        open={!!quoteProduct}
        onClose={() => setQuoteProduct(null)}
        productName={quoteProduct ?? ""}
      />
    </>
  );
};

export default ProductsSection;
